import { useEffect, useState } from "react";
import TextFieldDX from "../controls/textfielddx";
import GridDX from "../layout/griddx";
import AddEditEmbeddedModal from "../units_controls/addeditembeddedmodal";
import { useNotificationContext } from "../../context/notificationcontext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authcontext";
import { useTranslation } from "react-i18next";
import useUserService from "../../shared/services/userservices";
import { RegularExpressions, validatePassword } from "../../shared/globals";
import AutoCompleteMultiple from "../controls/autocompletemultiple";

const UserDetails = () => {
  const { state } = useLocation();
  const { userData } = useAuthContext();
  const navigate = useNavigate();
  const userId = state?.userId;
  const handleClose = () => {
    navigate(-1);
  };
  const { t } = useTranslation();

  const { setError, setInfo } = useNotificationContext();
  const { addUser, updateUser, getUserById, getRoles } = useUserService();
  const defaultValues = {
    userType: userData?.userType,
    customerId: userData?.customerId,
    tenantId: userData?.tenantId,
    email: "",
    password: "",
    fullName: "",
    mobile: "",
    jobTitle: "",
    isActive: true,
    roleIds:
      userData?.userType === "T"
        ? [
            {
              value: "8f62b822-a1a3-11ef-bf43-00ff4645e18f",
              text: t("Tenant"),
            },
          ]
        : userData?.userType === "C"
        ? [
            {
              value: "8f61738a-a1a3-11ef-bf43-00ff4645e18f",
              text: t("Customer"),
            },
          ]
        : [],
    // for put request
    oldPassword: "",
  };

  const [data, setData] = useState<any>(defaultValues);
  const [rolesDropdown, setRolesDropdown] = useState<any>([
    {
      value: "8f61738a-a1a3-11ef-bf43-00ff4645e18f",
      text: t("Customer"),
    },
    {
      value: "8f62b822-a1a3-11ef-bf43-00ff4645e18f",
      text: t("Tenant"),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    setIsLoading(true);
    const p1 = userId ? getUserById(userId) : null;
    const p2 = getRoles();
    Promise.all([p1, p2])
      .then(([user, roles]) => {
        if (userId) {
          const { roles, ...rest } = user;
          setData({
            ...rest,
            password: "",
            roleIds: roles.map((role: any) => ({
              value: role.id,
              text: role.name,
            })),
          });
        }
        setRolesDropdown(
          roles.map((role: any) => ({
            value: role.id,
            text: role.name,
          }))
        );
      })
      .catch((err) => setError(err))
      .finally(() => setIsLoading(false));
  }, []);

  const validateForm = () => {
    const newErrors: any = {};

    if (data.fullName.length === 0)
      newErrors["fullName"] = t("Name is required");
    if (data.password !== "") {
      const error = validatePassword(data.password);
      if (error !== "") newErrors["password"] = t(error);
    } else if (!userId && data.password.length === 0)
      newErrors["password"] = t("Password is required");
    if (data.email.length === 0) newErrors["email"] = t("Email is required");
    if (!RegularExpressions.emailAddress.test(data.email))
      newErrors["email"] = t("Invalid email address");

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const onSave = async () => {
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const { roleIds } = data;
    const roles = roleIds.map((e: any) => e.value);
    const operation = userId
      ? updateUser(userId, { ...data, roleIds: roles })
      : addUser({ ...data, roleIds: roles });
    operation
      .then(() => {
        setInfo(
          t(userId ? "User updated successfully" : "User created successfully")
        );
        handleClose();
      })
      .catch((err) => {
        if (err == "USER_ALREADY_EXISTS") {
          setError(t(`Email`) + " " + data.email + " " + t("is already taken"));
        } else if (err == "OLD_PASSWORD_DOESNOT_MATCH") {
          setError(t("Old password does not match"));
        } else {
          setError(err);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleInputChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    setIsChanged(true);
    setData({
      ...data,
      [name]: type == "checkbox" ? checked : value,
    });
  };

  return (
    <AddEditEmbeddedModal
      handleClose={handleClose}
      isSaving={isLoading}
      isChanged={isChanged}
      title={t("User Details")}
      onSaveClick={onSave}
      onYesClick={handleClose}
      isPrintable={false}
    >
      <GridDX container columnSpacing={1} rowSpacing={2}>
        <GridDX item xs={12} md={4}>
          <TextFieldDX
            label={t("Full Name")}
            name="fullName"
            value={data.fullName}
            onChange={handleInputChange}
            error={errors["fullName"]}
          />
        </GridDX>
        <GridDX item xs={12} md={4}>
          <TextFieldDX
            label={t("Email")}
            name="email"
            value={data.email}
            onChange={handleInputChange}
            error={errors["email"]}
          />
        </GridDX>
        <GridDX item xs={12} md={4}>
          <TextFieldDX
            label={t("Mobile")}
            name="mobile"
            value={data.mobile}
            onChange={handleInputChange}
            error={errors["mobile"]}
            type="number"
            // Disable spin buttons for number input
            className="noSpinButtons"
          />
        </GridDX>
        {userId && (
          <GridDX item xs={12} md={4}>
            <TextFieldDX
              label={t("Old Password")}
              name="oldPassword"
              value={data.oldPassword}
              onChange={handleInputChange}
              error={errors["oldPassword"]}
            />
          </GridDX>
        )}

        <GridDX item xs={12} md={4}>
          <TextFieldDX
            label={t("New Password")}
            name="password"
            value={data.password}
            onChange={handleInputChange}
            error={errors["password"]}
          />
        </GridDX>

        <GridDX item xs={12} md={4}>
          <TextFieldDX
            label={t("Job Title")}
            name="jobTitle"
            value={data.jobTitle}
            onChange={handleInputChange}
          />
        </GridDX>
        <GridDX item xs={12} md={4}>
          <AutoCompleteMultiple
            disabled={isLoading || userData?.userType !== "S"}
            label={t("Select Roles")}
            name="roleIds"
            value={data.roleIds}
            onChange={(newValue: any) => {
              setIsChanged(true);
              setData({
                ...data,
                roleIds: newValue,
              });
            }}
            error={errors["roleIds"]}
            list={rolesDropdown}
          />
        </GridDX>
      </GridDX>
    </AddEditEmbeddedModal>
  );
};

export default UserDetails;
